import { api } from "@/api/client";

// Shapes mirror LightreachImportSerializer / LightreachAr::BatchPlanner. Money
// is sent as decimal strings so nothing is lost to float rounding on the way.

export interface LrRow {
  transaction_id: string;
  finco_account_id: string;
  payout_event: string;
  description: string;
  amount: string;
  kind: string;
  first_name: string;
  last_name: string;
  address: string;
  serial_number?: string;
  // Safe Harbor Equipment rows carry no Lightreach account; matched from the row above.
  inferred_from?: string;
  inferred_ambiguous?: boolean;
  // A person moved it off the customer above; it's planned as its own project.
  detached?: boolean;
}

export interface LrInvoice {
  id: number;
  tranid: string;
  project_id: string;
  customer_id: number;
  customer_name: string;
  trandate: string;
  total: string;
  unpaid: string;
}

export interface LrApplication {
  invoice_id?: number;
  tranid?: string;
  record_key?: string;
  amount: string;
}

export type LrRecordType = "invoice" | "credit_memo" | "existing_credit_memo" | "price_correction" | "payment" | "refund";

export interface LrRecord {
  type: LrRecordType;
  key: string;
  external_id: string;
  amount: string;
  memo: string;
  item_name?: string;
  label?: string;
  // existing_credit_memo only: the credit memo accounting already booked.
  netsuite_id?: number;
  tranid?: string;
  applies: LrApplication[];
}

export interface LrFlag {
  code: string;
  message: string;
  blocking: boolean;
}

export interface LrProject {
  finco_account_id: string;
  project_id: string | null;
  project_source: "sunrise" | "manual" | null;
  customer: { lightreach_name: string; address: string; netsuite_id: number | null; netsuite_name: string | null };
  invoices: LrInvoice[];
  rows: LrRow[];
  // Rows paid and taken back in this same batch; neither is booked.
  offset_transaction_ids?: string[];
  mode: "net" | "refund";
  positive_total: string;
  negative_total: string;
  net_total: string;
  records: LrRecord[];
  flags: LrFlag[];
  already_booked: boolean;
  // "tool": this tool posted it; "hand": accounting booked it in NetSuite.
  booked_by?: "tool" | "hand" | null;
  blocked: boolean;
}

export interface LrPlan {
  lr_batch_id: string;
  approved_on: string;
  deposit_on: string;
  expected_total: string;
  deposit_total: string;
  refund_total: string;
  reconciled: boolean;
  already_booked: boolean;
  blocked: boolean;
  flags: LrFlag[];
  counts: Record<"projects" | "invoices" | "credit_memos" | "existing_credit_memos" | "price_corrections" | "payments" | "refunds" | "deposits", number>;
  projects: LrProject[];
  deposit: { key: string; external_id: string; amount: string; memo: string; payment_keys: string[] };
}

export type LrBatchStatus = "planned" | "posting" | "partly_posted" | "posted" | "failed" | "already_booked";

export interface LrProjectState {
  // left_for_hand: a poster will book it in NetSuite; the deposit went without it.
  status?: "posting" | "posted" | "failed" | "left_for_hand" | null;
  error?: string | null;
  posted_at?: string;
  posted_by_id?: number;
  acknowledged_by?: string;
  acknowledged_at?: string;
}

export interface LrBatch {
  id: number;
  lr_batch_id: string;
  approved_on: string;
  deposit_on: string;
  expected_total: string;
  status: LrBatchStatus;
  error_message: string | null;
  // finco => whether its Post button is live.
  postable: Record<string, boolean>;
  project_states: Record<string, LrProjectState>;
  deposit_ready: boolean;
  deposit_ready_with_open_items: boolean;
  open_projects: string[];
  posted_elsewhere: boolean;
  posted_at: string | null;
  posted_by: string | null;
  netsuite_deposit_id: number | null;
  posted: Record<string, number>;
  plan: Partial<LrPlan>;
}

export interface LrImport {
  id: number;
  cutoff_on: string | null;
  filename: string | null;
  row_count: number;
  status: "planning" | "planned" | "failed";
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  batch_count: number;
  netsuite_base_url?: string | null;
  write_enabled?: boolean;
  batches?: LrBatch[];
}

export async function fetchLightreachImports(): Promise<LrImport[]> {
  const { data } = await api.get<LrImport[]>("/lightreach_imports");
  return data;
}

export async function fetchLightreachImport(id: number): Promise<LrImport> {
  const { data } = await api.get<LrImport>(`/lightreach_imports/${id}`);
  return data;
}

export async function uploadLightreachExport(file: File): Promise<LrImport> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<LrImport>("/lightreach_imports", form);
  return data;
}

export interface BatchUpdate {
  deposit_on?: string;
  project?: {
    finco_account_id: string; sunrise_project_id?: string; accept_price_difference?: boolean; accept_fee_on_invoice?: boolean;
    accept_inferred_rows?: boolean; booked_by_hand?: boolean; detach_row?: string;
  };
}

export async function updateLightreachBatch(id: number, input: BatchUpdate): Promise<LrBatch> {
  const { data } = await api.patch<LrBatch>(`/lightreach_import_batches/${id}`, input);
  return data;
}

export async function replanLightreachBatch(id: number): Promise<LrBatch> {
  const { data } = await api.post<LrBatch>(`/lightreach_import_batches/${id}/replan`);
  return data;
}

export async function postLightreachProject(id: number, fincoAccountId: string): Promise<LrBatch> {
  const { data } = await api.post<LrBatch>(`/lightreach_import_batches/${id}/post_project`, { finco_account_id: fincoAccountId });
  return data;
}

// acknowledged: the open projects (finco ids) the poster will book by hand.
export async function postLightreachDeposit(id: number, acknowledged?: string[]): Promise<LrBatch> {
  const { data } = await api.post<LrBatch>(`/lightreach_import_batches/${id}/post_deposit`, acknowledged ? { acknowledged } : {});
  return data;
}
